import type React from 'react';
import { Link } from 'react-router-dom';
import { getLiveUrl, getAdminUrl } from '@/utils/crossAppLinks';
import Logo from '@showliner/shared/components/Logo';

/* ==========================================================================
   FOOTER — Grounded, paper-feel, like the bottom of a printed program.
   Full ledger rule above, mono labels for column heads, no card chrome.
   ========================================================================== */

const Footer: React.FC = () => {
  return (
    <footer className="border-t border-grey-200 bg-grey-100/60">
      <div className="mx-auto max-w-6xl px-4 sm:px-8 pt-14 pb-10">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-x-8 gap-y-10">
          {/* Brand */}
          <div className="col-span-2 md:col-span-5">
            <Link to="/" aria-label="Showliner home">
              <Logo variant="full" theme="light" className="h-7 w-auto" />
            </Link>
            <p className="mt-4 text-sm text-grey-700 leading-relaxed max-w-[34ch]">
              Booking and show-night software for independent venues and artists.
            </p>
          </div>

          {/* Product */}
          <div className="md:col-span-3">
            <p className="label-stamp text-grey-600 mb-4">Product</p>
            <ul className="space-y-2.5">
              <li>
                <Link to="/for-venues" className="text-sm text-grey-800 hover:text-ink transition-colors">
                  For venues
                </Link>
              </li>
              <li>
                <Link to="/for-performers" className="text-sm text-grey-800 hover:text-ink transition-colors">
                  For artists
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-sm text-grey-800 hover:text-ink transition-colors">
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/discover" className="text-sm text-grey-800 hover:text-ink transition-colors">
                  Discover
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div className="md:col-span-2">
            <p className="label-stamp text-grey-600 mb-4">More</p>
            <ul className="space-y-2.5">
              <li>
                <a href={getLiveUrl()} className="text-sm text-grey-800 hover:text-ink transition-colors">
                  Live rooms
                </a>
              </li>
              <li>
                <a href={getAdminUrl()} className="text-sm text-grey-800 hover:text-ink transition-colors">
                  Sign in
                </a>
              </li>
              <li>
                <Link to="/about" className="text-sm text-grey-800 hover:text-ink transition-colors">
                  About
                </Link>
              </li>
              <li>
                <Link to="/support" className="text-sm text-grey-800 hover:text-ink transition-colors">
                  Support
                </Link>
              </li>
              <li>
                <a href="mailto:hello@showliner.io" className="text-sm text-grey-800 hover:text-ink transition-colors">
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="md:col-span-2">
            <p className="label-stamp text-grey-600 mb-4">Legal</p>
            <ul className="space-y-2.5">
              <li>
                <Link to="/privacy" className="text-sm text-grey-800 hover:text-ink transition-colors">
                  Privacy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-sm text-grey-800 hover:text-ink transition-colors">
                  Terms
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-grey-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <p className="text-xs font-mono text-grey-600">
            © {new Date().getFullYear()} Showliner&nbsp;&nbsp;·&nbsp;&nbsp;A product of Swavely Creative Co.
          </p>
          <p className="text-xs font-mono text-grey-500">
            v1.0
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
